import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createDefaultOutputImage,
  getOutputBackPreviewUrl,
  getOutputImageSide,
  getOutputPreviewUrl,
  setAssetPreviewValue,
  setOutputImageSide,
} from "../../common/slices/specsEditor/reducers/images";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import { LayerModel, type LayerCompositeFill } from "./specsModel/LayerModel";
import type { SpecsModel } from "./specsModel/SpecsModel";
import type { SpecsCatalogFabric } from "./specsModel/types";
import {
  SpecsImageProcessingStatus,
  type GlobalOutputImageId,
  type OutputImage,
  type SpecsEditorSession,
  type SpecsLayer,
} from "./types";
import { useSpecsEditorMasks } from "./useSpecsEditorMasks";
import { useCompositeOutput } from "./useCompositePreview";

const GLOBAL_OUTPUT_IMAGE_CONFIG: Array<{
  id: GlobalOutputImageId;
  sideName: "front" | "back";
  role: "highlight" | "mask";
  label: string;
}> = [
  {
    id: "globalHighlightFront",
    sideName: "front",
    role: "highlight",
    label: "Global Highlight Front",
  },
  {
    id: "globalHighlightBack",
    sideName: "back",
    role: "highlight",
    label: "Global Highlight Back",
  },
  {
    id: "globalMaskFront",
    sideName: "front",
    role: "mask",
    label: "Global Mask Front",
  },
  {
    id: "globalMaskBack",
    sideName: "back",
    role: "mask",
    label: "Global Mask Back",
  },
];

type GlobalOutputImagesMap = Record<GlobalOutputImageId, OutputImage>;
type SpecsVersionModel = NonNullable<ReturnType<SpecsModel["getVersion"]>>;
type BoundElement = NonNullable<
  ReturnType<SpecsVersionModel["getElement"]>
>["element"];
type BoundStyle = NonNullable<ReturnType<BoundElement["getDefaultStyle"]>>;
type CompositeLayerInput = {
  maskUrl: string | null;
  highlightUrl: string | null;
};

function getModelLayerCompositeUrl(
  currentVersion: SpecsVersionModel | null | undefined,
  layer: SpecsLayer,
  role: "mask" | "highlight",
  sideName: "front" | "back"
) {
  if (!layer.modelLayerPath) {
    return null;
  }

  const modelLayer = currentVersion?.getLayer(layer.modelLayerPath)?.layer;
  const modelSide = modelLayer?.sides.find(
    (side) =>
      side.name === sideName &&
      (role === "mask" ? side.imageUrl : side.highlightUrl)
  );
  if (!modelSide) {
    return null;
  }

  return role === "mask" ? modelSide.imageUrl : modelSide.highlightUrl;
}

function setSingleSideOutputImageUrl(
  image: OutputImage,
  url: string | null,
  previewUrl?: string | null
) {
  const side = getOutputImageSide(image, "front");
  if (side) {
    side.outputImageUrl = url;
    side.aiTransform = {
      ...side.aiTransform,
      status: url
        ? SpecsImageProcessingStatus.DONE
        : SpecsImageProcessingStatus.IDLE,
      acceptedImageUrl: url,
      errorMessage: undefined,
    };
    setAssetPreviewValue(
      side as Record<string, any>,
      "outputImageUrl",
      previewUrl ?? null
    );
    setOutputImageSide(image, "front", side);
  }

  image.outputImageUrl = url;
  image.aiTransform = {
    ...image.aiTransform,
    status: url
      ? SpecsImageProcessingStatus.DONE
      : SpecsImageProcessingStatus.IDLE,
    acceptedImageUrl: url,
    errorMessage: undefined,
  };
  setAssetPreviewValue(
    image as Record<string, any>,
    "outputImageUrl",
    previewUrl ?? null
  );
}

function hasCompositeSource(layers: CompositeLayerInput[]) {
  return layers.some((layer) => layer.maskUrl || layer.highlightUrl);
}

function createGlobalOutputImagesMap(
  specsModel: SpecsModel | null,
  productVersion: string
): GlobalOutputImagesMap {
  const version = specsModel?.getVersion(productVersion) ?? null;

  return Object.fromEntries(
    GLOBAL_OUTPUT_IMAGE_CONFIG.map(({ id, sideName, role, label }) => {
      const image = createDefaultOutputImage(label, role);
      const side = version?.getGlobalSide(sideName) ?? null;
      const url = role === "highlight" ? side?.highlightUrl : side?.imageUrl;
      const previewUrl = (
        role === "highlight"
          ? side?.extra?.__previewHighlightUrl
          : side?.extra?.__previewImageUrl
      ) as string | null | undefined;

      setSingleSideOutputImageUrl(image, url ?? null, previewUrl ?? null);

      return [id, image];
    })
  ) as GlobalOutputImagesMap;
}

function reconcileGlobalOutputImages(
  current: GlobalOutputImagesMap,
  nextAuthoritative: GlobalOutputImagesMap
): GlobalOutputImagesMap {
  return Object.fromEntries(
    GLOBAL_OUTPUT_IMAGE_CONFIG.map(({ id }) => {
      const currentImage = current[id];
      const nextImage = structuredClone(currentImage ?? nextAuthoritative[id]);
      const authoredImage = nextAuthoritative[id];

      setSingleSideOutputImageUrl(
        nextImage,
        authoredImage.outputImageUrl,
        getOutputPreviewUrl(authoredImage)
      );
      if (
        !nextImage.sourceLayerId &&
        nextImage.referenceImageIds.length === 0
      ) {
        const side = getOutputImageSide(nextImage, "front");
        if (side) {
          side.aiTransform = {
            ...side.aiTransform,
            status: authoredImage.outputImageUrl
              ? SpecsImageProcessingStatus.DONE
              : SpecsImageProcessingStatus.IDLE,
            acceptedImageUrl: authoredImage.outputImageUrl,
            errorMessage: undefined,
          };
          setOutputImageSide(nextImage, "front", side);
        }
      }

      return [id, nextImage];
    })
  ) as GlobalOutputImagesMap;
}

function layerMatchesModelPath(
  layer: SpecsLayer,
  args: {
    partName: string;
    elementName: string;
    styleName: string;
    layerName: string;
  }
) {
  const path = layer.modelLayerPath;
  return (
    path?.partName === args.partName &&
    path.elementName === args.elementName &&
    path.styleName === args.styleName &&
    path.layerName === args.layerName
  );
}

function resolveModelLayerSessionColor(
  args: {
    partName: string;
    elementName: string;
    styleName: string;
    layerName: string;
  },
  layers: SpecsLayer[]
) {
  const sessionLayer = layers.find((layer) =>
    layerMatchesModelPath(layer, args)
  );

  if (!sessionLayer || LayerModel.isInheritedColor(sessionLayer.baseColor)) {
    return null;
  }

  return sessionLayer.baseColor;
}

function resolveStyleInheritedPreviewColor(args: {
  partName: string;
  elementName: string;
  style: BoundStyle;
  inheritFromGroup: string | null;
  layers: SpecsLayer[];
}): string | null {
  const sourceLayer = args.style.getInheritanceSourceLayer(
    args.inheritFromGroup
  );
  const sourceColor = sourceLayer
    ? resolveModelLayerSessionColor(
        {
          partName: args.partName,
          elementName: args.elementName,
          styleName: args.style.name,
          layerName: sourceLayer.name,
        },
        args.layers
      )
    : null;

  if (sourceColor) {
    return sourceColor;
  }

  if (args.inheritFromGroup) {
    return resolveStyleInheritedPreviewColor({
      ...args,
      inheritFromGroup: null,
    });
  }

  return null;
}

function resolveElementInheritedPreviewColor(args: {
  partName: string;
  element: BoundElement;
  inheritFromGroup: string | null;
  layers: SpecsLayer[];
}) {
  const defaultStyle = args.element.getDefaultStyle();
  return defaultStyle
    ? resolveStyleInheritedPreviewColor({
        partName: args.partName,
        elementName: args.element.name,
        style: defaultStyle,
        inheritFromGroup: args.inheritFromGroup,
        layers: args.layers,
      })
    : null;
}

function resolvePartInheritedPreviewColor(
  version: SpecsVersionModel,
  args: {
    partName: string;
    inheritFromGroup: string | null;
    layers: SpecsLayer[];
    visited: Set<string>;
  }
): string | null {
  const part = version.getPart(args.partName);
  if (!part) {
    return null;
  }

  const visitKey = `${part.name}:${args.inheritFromGroup ?? ""}`;
  if (args.visited.has(visitKey)) {
    return null;
  }
  args.visited.add(visitKey);

  const mainElement = part.getMainElement();
  if (!mainElement) {
    return null;
  }

  const localColor = resolveElementInheritedPreviewColor({
    partName: part.name,
    element: mainElement,
    inheritFromGroup: args.inheritFromGroup,
    layers: args.layers,
  });
  if (localColor) {
    return localColor;
  }

  if (!mainElement.inherit || !part.inheritFrom) {
    return null;
  }

  return resolvePartInheritedPreviewColor(version, {
    partName: part.inheritFrom,
    inheritFromGroup: null,
    layers: args.layers,
    visited: args.visited,
  });
}

function resolveLayerPreviewColor(
  currentVersion: SpecsVersionModel | null,
  layer: SpecsLayer,
  layers: SpecsLayer[]
) {
  if (!LayerModel.isInheritedColor(layer.baseColor)) {
    return layer.baseColor;
  }
  if (!currentVersion || !layer.modelLayerPath) {
    return LayerModel.FALLBACK_PREVIEW_COLOR;
  }

  const boundLayer = currentVersion.getLayer(layer.modelLayerPath);
  const resolvedColor = boundLayer
    ? resolvePartInheritedPreviewColor(currentVersion, {
        partName: boundLayer.part.name,
        inheritFromGroup: boundLayer.element.inheritFromGroup || null,
        layers,
        visited: new Set<string>(),
      })
    : null;

  return resolvedColor ?? LayerModel.FALLBACK_PREVIEW_COLOR;
}

function resolveLayerCompositeFill(
  currentVersion: SpecsVersionModel | null,
  layer: SpecsLayer,
  previewColor: string,
  fabrics: SpecsCatalogFabric[],
  sideName: "front" | "back"
): LayerCompositeFill {
  if (!currentVersion || !layer.modelLayerPath) {
    return {
      baseColor: previewColor,
      baseTextureUrl: null,
      skipFill: false,
    };
  }

  const boundLayer = currentVersion.getLayer(layer.modelLayerPath);
  if (!boundLayer) {
    return {
      baseColor: previewColor,
      baseTextureUrl: null,
      skipFill: false,
    };
  }

  return LayerModel.resolveCompositeFill({
    layer: boundLayer.layer,
    baseColor: layer.baseColor,
    previewColor,
    fabrics,
    sideName,
  });
}

function buildCompositeLayerInput(args: {
  currentVersion: SpecsVersionModel | null;
  layer: SpecsLayer;
  previewColor: string;
  fabrics: SpecsCatalogFabric[];
  sideName: "front" | "back";
}) {
  const fill = resolveLayerCompositeFill(
    args.currentVersion,
    args.layer,
    args.previewColor,
    args.fabrics,
    args.sideName
  );
  const getPreviewUrl =
    args.sideName === "front" ? getOutputPreviewUrl : getOutputBackPreviewUrl;

  return {
    baseColor: fill.baseColor,
    baseTextureUrl: fill.baseTextureUrl,
    skipFill: fill.skipFill,
    maskUrl:
      getPreviewUrl(args.layer.mask) ??
      getModelLayerCompositeUrl(
        args.currentVersion,
        args.layer,
        "mask",
        args.sideName
      ),
    highlightUrl:
      getPreviewUrl(args.layer.highlight) ??
      getModelLayerCompositeUrl(
        args.currentVersion,
        args.layer,
        "highlight",
        args.sideName
      ),
  };
}

type UseSpecsEditorGlobalOutputsArgs = {
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  identity: {
    productName: string;
    productVersion: string;
  };
  session: SpecsEditorSession;
  specsModel: SpecsModel | null;
  specsModelRevision: number;
  fabrics: SpecsCatalogFabric[];
  visibleLayers: SpecsLayer[];
  allVisibleLayers: SpecsLayer[];
};

export function useSpecsEditorGlobalOutputs({
  currentVersion,
  identity,
  session,
  specsModel,
  specsModelRevision,
  fabrics,
  visibleLayers,
  allVisibleLayers,
}: UseSpecsEditorGlobalOutputsArgs) {
  const dispatch = useDispatch();
  const authoredGlobalOutputImages = useMemo(
    () => createGlobalOutputImagesMap(specsModel, identity.productVersion),
    [identity.productVersion, specsModel, specsModelRevision]
  );
  const [globalOutputImages, setGlobalOutputImages] =
    useState<GlobalOutputImagesMap>(authoredGlobalOutputImages);
  const globalProductKeyRef = useRef<string>("");

  useEffect(() => {
    const productKey = `${identity.productName}:${identity.productVersion}`;
    const shouldReset = globalProductKeyRef.current !== productKey;
    globalProductKeyRef.current = productKey;
    setGlobalOutputImages((current) =>
      shouldReset
        ? authoredGlobalOutputImages
        : reconcileGlobalOutputImages(current, authoredGlobalOutputImages)
    );
  }, [
    authoredGlobalOutputImages,
    identity.productName,
    identity.productVersion,
  ]);

  const globalOutputImagesList = useMemo(
    () => GLOBAL_OUTPUT_IMAGE_CONFIG.map(({ id }) => globalOutputImages[id]),
    [globalOutputImages]
  );
  const allOutputImages = useMemo(
    () =>
      allVisibleLayers.flatMap((layer) =>
        [layer.highlight, layer.mask].filter((image): image is OutputImage =>
          Boolean(image)
        )
      ),
    [allVisibleLayers]
  );
  const editableOutputImages = useMemo(
    () => [...allOutputImages, ...globalOutputImagesList],
    [allOutputImages, globalOutputImagesList]
  );
  const resolvePreviewColor = useCallback(
    (layer: SpecsLayer) =>
      resolveLayerPreviewColor(currentVersion ?? null, layer, allVisibleLayers),
    [allVisibleLayers, currentVersion]
  );
  const buildCompositeLayers = useCallback(
    (layers: SpecsLayer[]) =>
      layers.map((layer) =>
        buildCompositeLayerInput({
          currentVersion: currentVersion ?? null,
          layer,
          previewColor: resolvePreviewColor(layer),
          fabrics,
          sideName: "front",
        })
      ),
    [currentVersion, fabrics, resolvePreviewColor]
  );
  const buildBackCompositeLayers = useCallback(
    (layers: SpecsLayer[]) =>
      layers.map((layer) =>
        buildCompositeLayerInput({
          currentVersion: currentVersion ?? null,
          layer,
          previewColor: resolvePreviewColor(layer),
          fabrics,
          sideName: "back",
        })
      ),
    [currentVersion, fabrics, resolvePreviewColor]
  );
  const globalHighlightFront = {
    highlightUrl: getOutputPreviewUrl(globalOutputImages.globalHighlightFront),
    maskUrl: getOutputPreviewUrl(globalOutputImages.globalMaskFront),
  };
  const globalHighlightBack = {
    highlightUrl: getOutputPreviewUrl(globalOutputImages.globalHighlightBack),
    maskUrl: getOutputPreviewUrl(globalOutputImages.globalMaskBack),
  };
  const frontComposite = useCompositeOutput(
    buildCompositeLayers(visibleLayers),
    globalHighlightFront
  );
  const backCompositeLayers = useMemo(
    () => buildBackCompositeLayers(visibleLayers),
    [buildBackCompositeLayers, visibleLayers]
  );
  const backComposite = useCompositeOutput(
    hasCompositeSource(backCompositeLayers) ? backCompositeLayers : [],
    globalHighlightBack
  );
  const globalFrontComposite = useCompositeOutput(
    buildCompositeLayers(allVisibleLayers),
    globalHighlightFront
  );
  const globalBackCompositeLayers = useMemo(
    () => buildBackCompositeLayers(allVisibleLayers),
    [allVisibleLayers, buildBackCompositeLayers]
  );
  const globalBackComposite = useCompositeOutput(
    hasCompositeSource(globalBackCompositeLayers)
      ? globalBackCompositeLayers
      : [],
    globalHighlightBack
  );
  const showBackComposite = hasCompositeSource(backCompositeLayers);
  const showGlobalBackComposite = hasCompositeSource(globalBackCompositeLayers);
  const previewComposite = useMemo(
    () => ({
      front: frontComposite,
      back: backComposite,
      showBack: showBackComposite,
    }),
    [backComposite, frontComposite, showBackComposite]
  );
  const globalPreviewComposite = useMemo(
    () => ({
      front: globalFrontComposite,
      back: globalBackComposite,
      showBack: showGlobalBackComposite,
    }),
    [globalBackComposite, globalFrontComposite, showGlobalBackComposite]
  );

  const getGlobalOutputImageKey = useCallback(
    (imageId: string): GlobalOutputImageId | null =>
      GLOBAL_OUTPUT_IMAGE_CONFIG.find(
        ({ id }) => globalOutputImages[id].id === imageId
      )?.id ?? null,
    [globalOutputImages]
  );

  const updateGlobalOutputImage = useCallback(
    (
      imageId: string,
      updater: (image: OutputImage, key: GlobalOutputImageId) => void
    ) => {
      const key = getGlobalOutputImageKey(imageId);
      if (!key) {
        return false;
      }

      setGlobalOutputImages((current) => {
        const nextImage = structuredClone(current[key]);
        updater(nextImage, key);
        return {
          ...current,
          [key]: nextImage,
        };
      });
      return true;
    },
    [getGlobalOutputImageKey]
  );

  const updateGlobalOutputImageUrl = useCallback(
    (key: GlobalOutputImageId, url: string | null) => {
      const sideName = key.endsWith("Back") ? "back" : "front";
      if (key.includes("Highlight")) {
        currentVersion?.setGlobalHighlightUrl(sideName, url);
      } else {
        currentVersion?.setGlobalMaskUrl(sideName, url);
      }
    },
    [currentVersion]
  );

  useSpecsEditorMasks({ session, layers: allVisibleLayers });

  return {
    allOutputImages,
    editableOutputImages,
    globalOutputImages,
    previewComposite,
    globalPreviewComposite,
    setProcessedImageUrl: (imageId: string, url: string, side?: string) => {
      const globalKey = getGlobalOutputImageKey(imageId);
      if (globalKey) {
        updateGlobalOutputImageUrl(globalKey, url);
      }
      if (
        updateGlobalOutputImage(imageId, (image) => {
          const nextSide = getOutputImageSide(image, side ?? "front");
          if (nextSide) {
            nextSide.outputImageUrl = url;
            setOutputImageSide(image, side ?? "front", nextSide);
          }
        })
      ) {
        return;
      }
      dispatch(specsEditorActions.setProcessedImageUrl({ imageId, url, side }));
    },
    setGlobalHighlightFrontUrl: (url: string | null) => {
      updateGlobalOutputImageUrl("globalHighlightFront", url);
      setGlobalOutputImages((current) => ({
        ...current,
        globalHighlightFront: {
          ...current.globalHighlightFront,
          outputImageUrl: url,
          sourceLayerId: null,
          referenceImageIds: [],
          aiTransform: {
            ...current.globalHighlightFront.aiTransform,
            status: url
              ? SpecsImageProcessingStatus.DONE
              : SpecsImageProcessingStatus.IDLE,
            acceptedImageUrl: url,
            errorMessage: undefined,
            generations: [],
          },
        },
      }));
    },
    setGlobalHighlightBackUrl: (url: string | null) => {
      updateGlobalOutputImageUrl("globalHighlightBack", url);
      setGlobalOutputImages((current) => ({
        ...current,
        globalHighlightBack: {
          ...current.globalHighlightBack,
          outputImageUrl: url,
          sourceLayerId: null,
          referenceImageIds: [],
          aiTransform: {
            ...current.globalHighlightBack.aiTransform,
            status: url
              ? SpecsImageProcessingStatus.DONE
              : SpecsImageProcessingStatus.IDLE,
            acceptedImageUrl: url,
            errorMessage: undefined,
            generations: [],
          },
        },
      }));
    },
    setGlobalMaskFrontUrl: (url: string | null) => {
      updateGlobalOutputImageUrl("globalMaskFront", url);
      setGlobalOutputImages((current) => ({
        ...current,
        globalMaskFront: {
          ...current.globalMaskFront,
          outputImageUrl: url,
          sourceLayerId: null,
          referenceImageIds: [],
          aiTransform: {
            ...current.globalMaskFront.aiTransform,
            status: url
              ? SpecsImageProcessingStatus.DONE
              : SpecsImageProcessingStatus.IDLE,
            acceptedImageUrl: url,
            errorMessage: undefined,
            generations: [],
          },
        },
      }));
    },
    setGlobalMaskBackUrl: (url: string | null) => {
      updateGlobalOutputImageUrl("globalMaskBack", url);
      setGlobalOutputImages((current) => ({
        ...current,
        globalMaskBack: {
          ...current.globalMaskBack,
          outputImageUrl: url,
          sourceLayerId: null,
          referenceImageIds: [],
          aiTransform: {
            ...current.globalMaskBack.aiTransform,
            status: url
              ? SpecsImageProcessingStatus.DONE
              : SpecsImageProcessingStatus.IDLE,
            acceptedImageUrl: url,
            errorMessage: undefined,
            generations: [],
          },
        },
      }));
    },
  };
}
